import React, { useCallback, useEffect, useRef, useState } from "react";
import { get_feeds_detail, get_feeds_list } from "src/DAL/Community/Community";
import CreatePost from "./CreatePost";
import OpenCreatePostCard from "./OpenCreatePostCard";
import PostCard from "./PostCard";
import { FeedDetailDialog } from "./FeedDetailPopup";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPostSkeleton from "src/design-system/components/GrPostSkeleton";
import GrButton from "src/design-system/components/GrButton";
import GrCard from "src/design-system/components/GrCard";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrIcon from "src/design-system/components/GrIcon";

// Empty feed texts: proposals for Mohsin (TASK-019), kept here so they are easy to change.
const EMPTY_FEED_TITLE = "No posts yet.";
const EMPTY_GROUP_TITLE = "No posts in this group yet.";
const EMPTY_FEED_ACTION = "Create post";

const listSx = { display: "flex", flexDirection: "column", gap: gr.space[4] };
const loadMoreSx = { display: "flex", justifyContent: "center" };
// The next page starts loading while the member still has about four posts to read (the Load More
// button is still this far below the screen), so the feed keeps going without a wait at the end.
const PREFETCH_MARGIN = "0px 0px 2000px 0px";

function MainPart({ showInfoBox, setShowInfoBox, userInfo, dashboard }) {
  const { selectedGroup } = useContentSetting();
  const { enqueueSnackbar } = useSnackbar();
  const [pageNumber, setPageNumber] = useState(0);
  const [totlePages, setTotlePages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [feedsList, setFeedsList] = useState([]);
  const [loadMorePath, setLoadMorePath] = useState("");
  const [isLoadingSection, setIsLoadingSection] = useState(false);
  const [feedType, setFeedType] = useState("general");
  const [formAction, setFormAction] = useState("ADD");
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedFeed, setSelectedFeed] = useState({});
  const [loadMore, setLoadMore] = useState();
  const [conditionMetOnce, setConditionMetOnce] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);

  const observer = useRef();
  const lastBookElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && totlePages > pageNumber) {
            loadMoreData();
          }
        },
        { rootMargin: PREFETCH_MARGIN }
      );
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, totlePages, pageNumber, loadMorePath, observer]
  );

  const handleCloseDetailBox = () => {
    setOpenDetailModal(false);
  };

  const handleShowDetails = (post) => {
    setSelectedPost(post);
    setOpenDetailModal(true);
  };

  const handleSelectedImage = (post, index) => {
    setSelectedIndex(index);
    setSelectedPost(post);
    setOpenDetailModal(true);
  };

  const handleSuccessDelete = (post) => {
    setOpenDetailModal(false);
    const updated_feed = feedsList.filter((feed) => feed._id !== post._id);
    setFeedsList(updated_feed);
  };

  const handleSuccessUpdate = async (post) => {
    handleUpdateSpecificFeed(post._id);
    setOpenDetailModal(false);
  };

  const handleUpdateSpecificFeed = async (id) => {
    const result = await get_feeds_detail(id);
    if (result.code === 200) {
      const data = feedsList.map((feed) => {
        if (feed._id === id) {
          return { ...result.feed, comments: result.feed.comments };
        } else {
          return feed;
        }
      });
      setFeedsList(data);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleUpdateShowAll = async (id, val) => {
    const data = feedsList.map((feed) => {
      if (feed._id === id) {
        return { ...feed, is_show_all: val };
      } else {
        return feed;
      }
    });
    setFeedsList(data);
  };

  const getFeedsListing = async () => {
    if (!conditionMetOnce) {
      setIsLoadingSection(true);
    }

    const result = await get_feeds_list(loadMore);

    if (result.code === 200) {
      const new_feeds = result.feeds.map((feed) => ({
        ...feed,
        is_show_all: false,
      }));
      setConditionMetOnce(true);
      const updatedList = !conditionMetOnce
        ? new_feeds
        : feedsList.concat(new_feeds);
      setFeedsList(dashboard ? [updatedList[0]] : updatedList);
      setPageNumber((prev) => prev + 1);
      setTotlePages(result.total_pages);
      setLoadMorePath(result.load_more);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }

    setIsLoadingMore(false);
    setIsLoadingSection(false);
  };

  const getFeedsListingOnAddFeed = async (path) => {
    setIsLoadingSection(true);
    const result = await get_feeds_list(path);
    if (result.code === 200) {
      setFeedsList(result.feeds);
      setPageNumber(0);
      setTotlePages(result.total_pages);
      setLoadMorePath(result.load_more);
      setIsLoadingMore(false);
      setIsLoadingSection(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoadingMore(false);
      setIsLoadingSection(false);
    }
  };

  const getFeedsListingOnDelete = async (id) => {
    const data = feedsList.filter((feed) => feed._id !== id);
    setFeedsList(data);
  };

  const loadMoreData = () => {
    setLoadMore(loadMorePath);
    setIsLoadingMore(true);
  };

  const handleOpenSimpleBox = () => {
    setOpenCreateModal(true);
    setFeedType("general");
  };

  const handleFeedEdit = (selected_feed) => {
    setSelectedFeed(selected_feed);
    setOpenCreateModal(true);
    setFormAction("EDIT");
  };

  useEffect(() => {
    const url = selectedGroup
      ? `api/feed/group/feed_list?limit=15&page=0&group=${selectedGroup}`
      : `api/feed/group/feed_list?limit=15&page=0`;
    setFeedsList([]);
    setPageNumber(0);
    setConditionMetOnce(false);
    setLoadMore(url);
  }, [selectedGroup]);

  useEffect(() => {
    if (loadMore) {
      getFeedsListing();
    }
  }, [loadMore]);

  // The dashboard keeps only the first post; an empty list there is [undefined].
  const posts = feedsList.filter(Boolean);
  const isEmpty = conditionMetOnce && !isLoadingSection && posts.length === 0;
  const emptyFeed = (
    <GrEmptyState
      compact={dashboard}
      icon={<GrIcon icon={icons.people} />}
      title={selectedGroup ? EMPTY_GROUP_TITLE : EMPTY_FEED_TITLE}
      action={
        dashboard ? null : (
          <GrButton variant="soft" onClick={handleOpenSimpleBox}>
            {EMPTY_FEED_ACTION}
          </GrButton>
        )
      }
    />
  );

  return (
    <>
      <Box sx={listSx}>
        {!dashboard && (
          <OpenCreatePostCard
            handleOpenSimpleBox={handleOpenSimpleBox}
            userInfo={userInfo}
          />
        )}
        {/* Post-shaped placeholders while the first page loads (also right after a group switch). */}
        {isLoadingSection ? (
          <GrPostSkeleton count={dashboard ? 1 : 2} />
        ) : (
          posts.map((feeds_list, index) => (
            <PostCard
              showInfoBox={showInfoBox}
              setShowInfoBox={setShowInfoBox}
              feeds_list={feeds_list}
              index={index}
              userInfo={userInfo}
              getFeedsListingOnDelete={getFeedsListingOnDelete}
              handleShowDetails={handleShowDetails}
              handleFeedEdit={handleFeedEdit}
              getFeedsListingOnAddFeed={getFeedsListingOnAddFeed}
              handleUpdateSpecificFeed={handleUpdateSpecificFeed}
              handleUpdateShowAll={handleUpdateShowAll}
              key={feeds_list._id || index}
              handleSelectedImage={handleSelectedImage}
              openDetailModal={openDetailModal}
              dashboard={dashboard}
            />
          ))
        )}
        {isEmpty && (dashboard ? emptyFeed : <GrCard>{emptyFeed}</GrCard>)}
        {/* The next page: a post placeholder while it loads, otherwise Load More (also the scroll trigger). */}
        {!dashboard && !isLoadingSection && totlePages > pageNumber ? (
          isLoadingMore ? (
            <GrPostSkeleton />
          ) : (
            <Box sx={loadMoreSx}>
              <GrButton
                ref={lastBookElementRef}
                variant="outline"
                onClick={loadMoreData}
                id="load-more-feed"
              >
                Load More
              </GrButton>
            </Box>
          )
        ) : null}
      </Box>
      <CreatePost
        setFeedType={setFeedType}
        feedType={feedType}
        openCreateModal={openCreateModal}
        setOpenCreateModal={setOpenCreateModal}
        getFeedsListing={getFeedsListingOnAddFeed}
        userInfo={userInfo}
        selectedFeed={selectedFeed}
        formAction={formAction}
        setFormAction={setFormAction}
        handleSuccessUpdate={handleSuccessUpdate}
      />
      <FeedDetailDialog
        open={openDetailModal}
        onClose={handleCloseDetailBox}
        post_id={selectedPost._id}
        feed={selectedPost}
        getFeedsListingOnAddFeed={handleUpdateSpecificFeed}
        handleSuccessDelete={handleSuccessDelete}
        handleSuccessUpdate={handleSuccessUpdate}
        selectedImageIndex={selectedIndex}
      />
    </>
  );
}

export default MainPart;
